import { publicOrigin } from '@maxpreps/network/src/origins';
import useRequestContext from '@maxpreps/shared/contexts/RequestContext';
import React from 'react';
import { addGlobalQueryParams } from '../../functions/primitiveHelpers/urlHelpers';
import StyledAnchor, {
  IStyledAnchorProps
} from '../../ui/StyledAnchor/StyledAnchor';
import Link from '../Link/Link';
import { fromEntries } from './../../functions/primitiveHelpers/objectHelpers';

/* eslint-disable react/display-name */

interface ISmartAnchorPropOptions {
  as?: string;
  isTrusted?: boolean;
}

// eslint-disable-next-line prettier/prettier
export interface ISmartAnchorProps extends IStyledAnchorProps {
  visualComponent?: Function;
  visualComponentProps?: any;
  as?: string;
  options?: ISmartAnchorPropOptions;
}

const buildHrefProp = (href: string, appType) => {
  if (!appType) return href;
  return addGlobalQueryParams(href, appType);
};

const buildRelProp = (options: { isMaxpreps: boolean; isTrusted: boolean }) => {
  if (options?.isMaxpreps) return undefined;
  if (options?.isTrusted) return 'noopener';
  return 'noopener noreferrer nofollow';
};

const buildTargetProp = (options: { isMaxpreps: boolean; target: string }) => {
  if (options?.target) return options.target;
  if (!options?.isMaxpreps) return '_blank';
  return undefined;
};

const isMaxprepsRoute = (url: URL) => {
  const hostname = url.hostname.toLowerCase();
  return hostname.endsWith('.maxpreps.com') && !hostname.includes('support');
};

// Dan 9/22/21 - I do not like the pattern of conflating "rest" and "options"
// These helper functions were changed to accept an options object instead of args
// which broke the ability to pass "rest" props to the anchor.
// There is a quick patch on it now, but I want to clean up this logic again to make it more intuitive
const getAnchorProps = (
  href?: string,
  appType?: number,
  rest: any = {}
): any => {
  // No href
  if (!href) {
    console.error('No href supplied to anchor', href);
    return { href: publicOrigin };
  }

  // Invalid href
  let url: URL;
  try {
    url = new URL(href, publicOrigin);
  } catch (e) {
    console.error(e);
    return { href: publicOrigin };
  }

  const { as, isTrusted, target } = rest;

  const isMaxpreps = isMaxprepsRoute(url);

  return {
    href: buildHrefProp(href, appType),
    as,
    rel: buildRelProp({ isMaxpreps, isTrusted }),
    target: buildTargetProp({ isMaxpreps, target }),
    ...rest
  };
};

const SmartAnchor = React.forwardRef<HTMLAnchorElement, ISmartAnchorProps>(
  (props, ref) => {
    const {
      href,
      visualComponent,
      visualComponentProps,
      children,
      className,
      ...rest
    } = props;

    const { appType } = useRequestContext();

    const events = fromEntries(
      Object.entries(rest).filter(kvp => kvp[0].startsWith('on'))
    );

    const {
      href: modifiedHref,
      as,
      ...anchorProps
    } = getAnchorProps(href, appType, rest);

    const Component = (visualComponent ?? StyledAnchor) as any;

    const immediateChild = (
      <Component
        {...events}
        {...anchorProps}
        {...(visualComponentProps ?? {})}
        as="a"
        href={modifiedHref}
        className={className}
        ref={ref}
      >
        {children}
      </Component>
    );

    if (as) {
      return (
        <Link href={modifiedHref} as={as}>
          {immediateChild}
        </Link>
      );
    }

    return immediateChild;
  }
);

SmartAnchor.displayName = 'SmartAnchor';
export default SmartAnchor;
